import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CloudUploadIcon } from 'lucide-react'
import { account } from '../../../../classes/Account'
import { refreshAccessToken } from '../../../../utilities/extensions'
import { apiURL } from '../../../../utilities/URLs'
import { Document } from '../classes/Document'
import DocumentRow from './DocumentRow'
import { labelForTranscriptDocumentType } from '../utils/transcriptDocumentUploadCatalog'

const QUARTER_LABELS = {
  q12020: 'Q1 2020',
  q22020: 'Q2 2020',
  q32020: 'Q3 2020',
  q42020: 'Q4 2020',
  q12021: 'Q1 2021',
  q22021: 'Q2 2021',
  q32021: 'Q3 2021',
  q42021: 'Q4 2021'
}

function RequirementBlock({ applicationId, quarterKey, documentType, requirement, onChanged }) {
  const navigate = useNavigate()
  const [documents, setDocuments] = useState([])

  const loadDocs = useCallback(async () => {
    const qParam = `&quarter=${encodeURIComponent(quarterKey)}`
    const res = await fetch(
      `${apiURL}/api/applications/${applicationId}/documents?type=${encodeURIComponent(documentType)}${qParam}`,
      { headers: { Authorization: account.accessToken } }
    )
    const data = await res.json()
    if (data.response?.error === 'Failed To Authenticate Token.') {
      const refreshed = await refreshAccessToken()
      if (refreshed) return loadDocs()
      navigate('/', { state: { expired: true } })
      return
    }
    if (!data.response?.error)
      setDocuments(data.response?.documents || [])
  }, [applicationId, documentType, quarterKey, navigate])

  useEffect(() => {
    loadDocs()
  }, [loadDocs])

  const handleUploadFile = async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const uploadResponse = await fetch(
      `${apiURL}/api/applications/${applicationId}/documents?type=${encodeURIComponent(documentType)}&quarter=${encodeURIComponent(quarterKey)}`,
      { method: 'POST', headers: { Authorization: account.accessToken }, body: formData }
    )
    const uploadData = await uploadResponse.json()
    if (uploadData.response?.error) {
      if (uploadData.response?.error === 'Failed To Authenticate Token.') {
        const refreshed = await refreshAccessToken()
        if (refreshed) return handleUploadFile(file)
        navigate('/', { state: { expired: true } })
      } else
        console.error(uploadData.response.error)
    } else {
      setDocuments(prev => [...prev, new Document({
        documentId: uploadData.response.documentId,
        applicationId,
        name: file.name,
        size: file.size,
        mimetype: file.type,
        documentURL: uploadData.response.documentURL
      })])
      onChanged()
    }
  }

  const handleFileUpload = (files) => {
    if (!files) return
    Array.from(files).forEach(file => { handleUploadFile(file) })
  }

  const title = labelForTranscriptDocumentType(documentType)
  const reqLabel = requirement === 'required' ? 'Required' : 'Optional but preferred'
  const reqClass = requirement === 'required'
    ? 'bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-100'
    : 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100'

  return (
    <div className='rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800/80 p-4'>
      <div className='flex flex-wrap items-start justify-between gap-2 mb-3'>
        <div>
          <p className='text-sm font-semibold text-slate-900 dark:text-white'>{title}</p>
        </div>
        <span className={`text-xs font-semibold px-2 py-1 rounded-md shrink-0 ${reqClass}`}>{reqLabel}</span>
      </div>
      <label
        htmlFor={`tr-upload-${quarterKey}-${documentType}`}
        className='w-full border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-xl p-4 flex flex-col items-center text-center bg-gray-50 dark:bg-slate-900/50 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors'
      >
        <CloudUploadIcon size={24} className='text-slate-400 mb-1' />
        <span className='text-sm font-medium text-slate-700 dark:text-gray-200'>Upload files</span>
      </label>
      <input
        id={`tr-upload-${quarterKey}-${documentType}`}
        type='file'
        multiple
        className='hidden'
        onChange={e => handleFileUpload(e.target.files)}
      />
      <div className='mt-3 space-y-2'>
        {documents.length === 0 ? (
          <p className='text-xs text-slate-500 dark:text-slate-400 italic text-center py-2'>No files yet.</p>
        ) : (
          <ul className='space-y-2'>
            {documents.map(doc => (
              <DocumentRow
                key={doc.documentId || doc.document_id}
                applicationId={applicationId}
                _document={doc}
                setDocuments={setDocuments}
                handleCheckDocuments={onChanged}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

/**
 * @param {Object} props
 * @param {string} props.applicationId
 * @param {Array<{ quarterKey: string, documentType: string, requirement: string }>} props.requirements
 * @param {() => void} props.onChanged
 */
export default function TranscriptRequirementUploads({ applicationId, requirements, onChanged }) {
  const byQuarter = useMemo(() => {
    const m = {}
    for (const r of requirements) {
      if (!m[r.quarterKey]) m[r.quarterKey] = []
      m[r.quarterKey].push(r)
    }
    return m
  }, [requirements])

  const quarterKeys = useMemo(
    () => Object.keys(byQuarter).sort(),
    [byQuarter]
  )

  const [activeQuarter, setActiveQuarter] = useState(() => quarterKeys[0] || '')

  useEffect(() => {
    if (quarterKeys.length === 0) {
      setActiveQuarter('')
      return
    }
    if (!quarterKeys.includes(activeQuarter))
      setActiveQuarter(quarterKeys[0])
  }, [quarterKeys, activeQuarter])

  const activeRows = activeQuarter ? byQuarter[activeQuarter] || [] : []

  return (
    <div className='w-full space-y-6 mt-8'>
      <div className='rounded-xl border border-ertc-primary/30 bg-ertc-primary/5 dark:bg-ertc-primary/10 px-4 py-3 text-sm text-slate-800 dark:text-slate-100'>
        <p className='font-semibold'>Documents requested for your application</p>
        <p className='text-xs mt-1 text-slate-600 dark:text-slate-300'>
          Choose each quarter tab, then upload the files listed. <span className='font-medium'>Required</span> items must be received for every quarter before you can complete this step; <span className='font-medium'>Optional but preferred</span> items help us process your file faster if you can provide them.
        </p>
      </div>

      <div className='w-full overflow-x-auto'>
        <div
          className='flex flex-nowrap md:flex-wrap justify-start border-b border-gray-200 dark:border-slate-700 gap-2 custom-scrollbar'
          role='tablist'
          aria-label='Quarters with document requests'
        >
          {quarterKeys.map(qk => {
            const isActive = qk === activeQuarter
            return (
              <button
                key={qk}
                type='button'
                role='tab'
                aria-selected={isActive}
                id={`tr-quarter-tab-${qk}`}
                onClick={() => setActiveQuarter(qk)}
                className={`flex-shrink-0 px-3 py-2 md:px-5 md:py-3 rounded-t-xl whitespace-nowrap transition-colors duration-200 min-h-11 focus:outline-none focus-visible:ring-2 focus-visible:ring-ertc-primary focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900 ${isActive
                  ? 'border-b-2 border-ertc-primary text-ertc-primary font-bold bg-gray-50 dark:bg-slate-800'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-slate-800'
                  }`}
              >
                {QUARTER_LABELS[qk] || qk}
              </button>
            )
          })}
        </div>
      </div>

      {activeQuarter ? (
        <section
          role='tabpanel'
          aria-labelledby={`tr-quarter-tab-${activeQuarter}`}
          className='space-y-4'
        >
          <div className='grid gap-4 md:grid-cols-2'>
            {activeRows.map((req, idx) => (
              <RequirementBlock
                key={`${activeQuarter}-${req.documentType}-${idx}`}
                applicationId={applicationId}
                quarterKey={activeQuarter}
                documentType={req.documentType}
                requirement={req.requirement}
                onChanged={onChanged}
              />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  )
}
